import React, { useEffect, useState } from 'react'

import { setPlanningShiftTableLoading } from '@/entities/gridTables/slices/planningShiftTablesSlice'
import { moveBreak } from '@/entities/gridTables/thunks/moveBreak'
import { processDropOnShift, ProcessDropOnShiftPayload } from '@/entities/gridTables/thunks/processDropOnShift'
import { updatePlanningShiftTableState } from '@/entities/gridTables/thunks/updatePlanningShiftTableState'
import { fetchScenarios } from '@/entities/schedules'
import { upsertOneWidgetWindow } from '@/entities/widgetWindows'
import { GanttChart } from '@/shared/components/GanttChart/GanttChart'
import { TimelineStop } from '@/shared/components/GanttChart/models/GanttRow'
import { WidgetTypeWithSuffix } from '@/shared/components/Mosaic'
import { TOOL_WIDGETS } from '@/shared/components/Mosaic/models/widgetType'
import { useAppDispatch } from '@/shared/store'
import { faChartBar } from '@fortawesome/pro-regular-svg-icons'

import { usePlanningGanttChartData } from './libs/usePlanningGanttChartData'
import { useOperationsGanttContext } from './OperationsGanttChartContext'

export type PlanningGanttChartProps = {
  id: string
  widgetId: WidgetTypeWithSuffix // Widget Window id
}

export const PlanningGanttChart: React.FC<PlanningGanttChartProps> = ({ widgetId }) => {
  const { setWidgetId } = useOperationsGanttContext()
  const { rows, minStartTime, maxEndTime } = usePlanningGanttChartData({ widgetId })
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(false)

  // Update widget window context and global store to render widow title, uiColor, etc.
  useEffect(() => {
    if (widgetId) {
      setWidgetId(widgetId as `GanttChart-${string}`)
      dispatch(upsertOneWidgetWindow({
        id: widgetId,
        shiftIds: [],
        isActive: true,
        widgetType: TOOL_WIDGETS.GanttChart,
        navigations: [
          {
            title: 'Gantt Chart',
            icon: faChartBar,
          },
        ],
      }))
    }
  }, [widgetId, dispatch, setWidgetId])

  const onStopDrop = async (sourceStop: TimelineStop, targetStop: TimelineStop) => {
    if (loading) return

    const sourceShiftId = sourceStop.shiftId
    const targetShiftId = targetStop.shiftId
    const isSourceBreak = sourceStop.displayStopType === 'Break'
    const sourceStopIds = [sourceStop.id]
    const targetStopId = targetStop.id

    const payload: ProcessDropOnShiftPayload = {
      orderIds: null,
      sourceShiftId,
      shiftId: targetShiftId,
      stopId: targetStopId,
      stopIds: sourceStopIds,
    }

    // Set loading state for Gantt and Shift tables
    setLoading(true)
    dispatch(setPlanningShiftTableLoading({ id: targetStop.shiftId, isLoading: true }))

    if (payload.sourceShiftId) {
      dispatch(setPlanningShiftTableLoading({ id: payload.sourceShiftId, isLoading: true }))
    }

    // We only need to update the target shift table state on planning screen
    //    because we are copying the orders from source shift to target shift
    if (isSourceBreak) {
      await dispatch(moveBreak({ stopId: sourceStop.id, targetStopId: targetStop.id }))
      await dispatch(fetchScenarios({ scenarioIds: [sourceStop.scheduleId] }))
    }
    else {
      await dispatch(processDropOnShift(payload))
      await dispatch(fetchScenarios({ scenarioIds: [targetStop.scheduleId, sourceStop.scheduleId] }))
    }

    await Promise.allSettled([
      dispatch(updatePlanningShiftTableState({ shiftId: targetStop.shiftId })),
      payload.sourceShiftId ? dispatch(updatePlanningShiftTableState({ shiftId: payload.sourceShiftId })) : Promise.resolve(),
    ])

    // Reset loading state
    setLoading(false)
    dispatch(setPlanningShiftTableLoading({ id: targetStop.shiftId, isLoading: false }))

    if (payload.sourceShiftId) {
      dispatch(setPlanningShiftTableLoading({ id: payload.sourceShiftId, isLoading: false }))
    }
  }

  return (
    <GanttChart
      rows={rows}
      startHour={Math.max(0, minStartTime.hour() - 1)}
      endHour={Math.min(24, maxEndTime.hour() + 1)}
      onStopDrop={onStopDrop}
      loading={loading}
    />
  )
}
